import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Form, Input, Button, message, Table, Col, Row } from 'antd';
import axios from 'axios';
import * as XLSX from 'xlsx';

interface Employee {
    id: string;
    displayName: string;
    email: string;
    officeLocation: string;
    isManager: boolean;
    managerBudget: number;
    managerUsedBudget: number;
    managerBalanceBudget: number;
    managerId: string;
}

interface BudgetData {
    id?: string;
    unitBudgetId: string;
    unitHeadName: string;
    unitHeadUserId: string;
    unitHeadOfficeLocation: string;
    awardsCount: number;
    awardsUsed: number;
    employees: Employee[];
}

const BudgetReport: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [reportData, setReportData] = useState<BudgetData[]>([]);
  const [showReport, setShowReport] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  useEffect(() => {
    fetchToken();
  }, []);

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  };

  const handleSubmit = async () => {
    const API_URL = process.env.REACT_APP_API_URL;

    try {
      const token = await fetchToken();
      const response = await axios.get(`${API_URL}/api/Budget/full-budget-report`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      // Add unique ID to each record for stable rowKey
      const dataWithIds = response.data.map((item: BudgetData, index: number) => ({
        ...item,
        id: `${item.unitHeadName}-${item.unitBudgetId}-${index}`
      }));
      setReportData(dataWithIds);
      setShowReport(true);
      console.log('Budget Report Data:', response.data);
    } catch (error) {
      message.error(`Failed to get report data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDownloadExcel = () => {
    if (reportData.length === 0) {
      message.error('No data available to download');
      return;
    }

    const formattedData: any[] = [];
    reportData.forEach((item) => {
      formattedData.push({
        'Unit Head': item.unitHeadName,
        'Awards Count': item.awardsCount,
        'Awards Used': item.awardsUsed,
        'Remaining': item.awardsCount - item.awardsUsed,
      });
      item.employees.forEach((emp) => {
        formattedData.push({
          'Unit Head': `  - ${emp.displayName}`,
          'Awards Count': emp.managerBudget,
          'Awards Used': emp.managerUsedBudget,
          'Remaining': emp.managerBalanceBudget,
        });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'BudgetReport');

    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];

    XLSX.writeFile(workbook, `BudgetReport_${formattedDate}.xlsx`);
  };

  const employeeColumns = [
    {
      title: 'Manager Name',
      dataIndex: 'displayName',
      key: 'displayName',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Location',
      dataIndex: 'officeLocation',
      key: 'officeLocation',
    },
    {
      title: 'Manager Budget',
      dataIndex: 'managerBudget',
      key: 'managerBudget',
      render: (value: number) => value?.toFixed(2) || '0.00',
    },
    {
      title: 'Used Budget',
      dataIndex: 'managerUsedBudget',
      key: 'managerUsedBudget',
      render: (value: number) => value?.toFixed(2) || '0.00',
    },
    {
      title: 'Balance Budget',
      dataIndex: 'managerBalanceBudget',
      key: 'managerBalanceBudget',
      render: (value: number) => value?.toFixed(2) || '0.00',
    },
  ];

  const columns = [
    {
      title: 'Unit Head Name',
      dataIndex: 'unitHeadName',
      key: 'unitHeadName',
      sorter: (a: BudgetData, b: BudgetData) => a.unitHeadName.localeCompare(b.unitHeadName),
    },
    {
      title: 'Office Location',
      dataIndex: 'unitHeadOfficeLocation',
      key: 'unitHeadOfficeLocation',
      sorter: (a: BudgetData, b: BudgetData) => (a.unitHeadOfficeLocation || '').localeCompare(b.unitHeadOfficeLocation || ''),
    },
    {
      title: 'Awards Count',
      dataIndex: 'awardsCount',
      key: 'awardsCount',
      sorter: (a: BudgetData, b: BudgetData) => a.awardsCount - b.awardsCount,
      render: (value: number) => value?.toFixed(2) || '0.00',
    },
    {
      title: 'Awards Used',
      dataIndex: 'awardsUsed',
      key: 'awardsUsed',
      sorter: (a: BudgetData, b: BudgetData) => a.awardsUsed - b.awardsUsed,
      render: (value: number) => value || 0,
    },
    {
      title: 'Remaining',
      key: 'remaining',
      render: (record: BudgetData) => (record.awardsCount - record.awardsUsed).toFixed(2),
    },
    {
      title: 'Total Managers',
      key: 'employeeCount',
      render: (record: BudgetData) => record.employees?.length || 0,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <Button type="primary" onClick={handleSubmit}>
          Get Report
        </Button>
        <Button type="default" onClick={handleDownloadExcel} style={{ marginLeft: '16px' }}>
          Download as Excel
        </Button>
      </div>
      {showReport && (
        <div>
          <Table
            dataSource={reportData}
            columns={columns}
            rowKey="id"
            expandable={{
              expandedRowRender: (record: BudgetData) => (
                <div style={{ margin: 0, padding: '0 20px' }}>
                  <h4 style={{ marginBottom: 10 }}>Managers under {record.unitHeadName}</h4>
                  <Table
                    dataSource={record.employees}
                    columns={employeeColumns}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                </div>
              ),
              rowExpandable: (record: BudgetData) => record.employees && record.employees.length > 0,
            }}
            pagination={{ 
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
              onShowSizeChange: (current, size) => {
                setCurrentPage(1);
                setPageSize(size);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default BudgetReport;
